import type { DocumentEncoding } from "./encodingLabel.js";
import type { EolStyle } from "./eol.js";
import type { LanguageMode } from "./languageMode.js";
import { inferLanguageModeFromFilename } from "./inferLanguageFromFilename.js";
import type { DocumentManager, TabDocument } from "./DocumentManager.js";
import type { DiffEditorSnapshot } from "../ui/diffTabMonaco.js";
import type { TabColorId } from "./tabColors.js";

const TAB_SESSION_KEY = "npp-web-tab-session-v1";
/** Stay under typical 5MB localStorage limits. */
const MAX_STORAGE_CHARS = 4_400_000;

export type PersistedRow =
  | {
      kind: "text";
      id: string;
      title: string;
      content: string;
      savedContent: string;
      dirty: boolean;
      filePath: string | null;
      encoding: DocumentEncoding;
      eol: EolStyle;
      language: LanguageMode;
      /** Absent in older snapshots → treated as null. */
      tabColor?: TabColorId | null;
      /** Absent unless tab came from share inbox. */
      fromShare?: boolean;
    }
  | {
      kind: "diff";
      id: string;
      title: string;
      diffPair: DiffEditorSnapshot;
      diffSplitView: boolean;
      tabColor: TabColorId | null;
    }
  | {
      kind: "placeholder";
      id: string;
      title: string;
      preview: "image" | "pdf";
      tabColor: TabColorId | null;
      /** Full display path stored when the tab had one (e.g. workspace-relative). Absent in older snapshots. */
      filePath?: string | null;
    };

export type TabSessionSnapshotV1 = {
  v: 1;
  activeIndex: number;
  untitledSeq: number;
  idSeq: number;
  rows: PersistedRow[];
};

function docToRow(d: TabDocument): PersistedRow {
  if (d.sessionRestorePlaceholder) {
    return {
      kind: "placeholder",
      id: d.id,
      title: d.title,
      preview: d.sessionRestorePlaceholder,
      tabColor: d.tabColor,
      ...(d.filePath ? { filePath: d.filePath } : {}),
    };
  }
  if (d.viewMode === "text") {
    return {
      kind: "text",
      id: d.id,
      title: d.title,
      content: d.content,
      savedContent: d.savedContent,
      dirty: d.dirty,
      filePath: d.filePath,
      encoding: d.encoding,
      eol: d.eol,
      language: d.language,
      tabColor: d.tabColor,
      ...(d.fromShare ? { fromShare: true as const } : {}),
    };
  }
  if (d.viewMode === "diff" && d.diffPair) {
    return {
      kind: "diff",
      id: d.id,
      title: d.title,
      diffPair: d.diffPair,
      diffSplitView: d.diffSplitView,
      tabColor: d.tabColor,
    };
  }
    const preview = d.viewMode === "pdf" ? "pdf" : "image";
  return {
    kind: "placeholder",
    id: d.id,
    title: d.title,
    preview,
    tabColor: d.tabColor,
    ...(d.filePath ? { filePath: d.filePath } : {}),
  };
}

function rowToTab(row: PersistedRow): TabDocument {
  if (row.kind === "text") {
    return {
      id: row.id,
      title: row.title,
      content: row.content,
      savedContent: row.savedContent,
      dirty: row.dirty,
      filePath: row.filePath,
      fileHandle: null,
      encoding: row.encoding,
      eol: row.eol,
      history: [row.content],
      historyIndex: 0,
      language: row.language,
      viewMode: "text",
      previewObjectUrl: null,
      diffPair: null,
      diffSplitView: false,
      tabColor: (row.tabColor ?? null) as TabColorId | null,
      ...(row.fromShare ? { fromShare: true as const } : {}),
      fileSourceMtimeMs: null,
      diskChangedWhileDirty: false,
    };
  }
  if (row.kind === "diff") {
    return {
      id: row.id,
      title: row.title,
      content: "",
      savedContent: "",
      dirty: false,
      filePath: null,
      fileHandle: null,
      encoding: "utf-8",
      eol: "lf",
      history: [""],
      historyIndex: 0,
      language: "diff",
      viewMode: "diff",
      previewObjectUrl: null,
      diffPair: row.diffPair,
      diffSplitView: row.diffSplitView,
      tabColor: row.tabColor,
      fileSourceMtimeMs: null,
      diskChangedWhileDirty: false,
    };
  }
  const language = inferLanguageModeFromFilename(row.title);
  return {
    id: row.id,
    title: row.title,
    content: "",
    savedContent: "",
    dirty: false,
    filePath: row.filePath ?? null,
    fileHandle: null,
    encoding: "utf-8",
    eol: "lf",
    history: [""],
    historyIndex: 0,
    language,
    viewMode: "text",
    previewObjectUrl: null,
    diffPair: null,
    diffSplitView: false,
    tabColor: row.tabColor,
    fileSourceMtimeMs: null,
    diskChangedWhileDirty: false,
    sessionRestorePlaceholder: row.preview,
  };
}

function isPlainRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

function isPersistedRow(x: unknown): x is PersistedRow {
  if (!isPlainRecord(x) || typeof x.kind !== "string") return false;
  if (x.kind === "text") {
    return (
      typeof x.id === "string" &&
      typeof x.title === "string" &&
      typeof x.content === "string" &&
      typeof x.savedContent === "string" &&
      typeof x.dirty === "boolean" &&
      (x.filePath === null || typeof x.filePath === "string") &&
      typeof x.encoding === "string" &&
      typeof x.eol === "string" &&
      typeof x.language === "string" &&
      (x.fromShare === undefined || typeof x.fromShare === "boolean")
    );
  }
  if (x.kind === "diff") {
    const p = x.diffPair;
    return (
      typeof x.id === "string" &&
      typeof x.title === "string" &&
      isPlainRecord(p) &&
      typeof p.leftLabel === "string" &&
      typeof p.rightLabel === "string" &&
      typeof p.leftText === "string" &&
      typeof p.rightText === "string" &&
      typeof p.monacoLanguage === "string" &&
      typeof p.highlightLanguage === "string" &&
      typeof x.diffSplitView === "boolean"
    );
  }
  if (x.kind === "placeholder") {
    return (
      typeof x.id === "string" &&
      typeof x.title === "string" &&
      (x.preview === "image" || x.preview === "pdf") &&
      (x.filePath === undefined || x.filePath === null || typeof x.filePath === "string")
    );
  }
  return false;
}

function parseSnapshot(raw: string): TabSessionSnapshotV1 | null {
  try {
    const data: unknown = JSON.parse(raw);
    if (!isPlainRecord(data) || data.v !== 1) return null;
    if (
      typeof data.activeIndex !== "number" ||
      !Number.isFinite(data.activeIndex) ||
      !Array.isArray(data.rows)
    ) {
      return null;
    }
    if (
      typeof data.untitledSeq !== "number" ||
      typeof data.idSeq !== "number" ||
      !Number.isFinite(data.untitledSeq) ||
      !Number.isFinite(data.idSeq)
    ) {
      return null;
    }
    const rows = data.rows as unknown[];
    if (!rows.every(isPersistedRow)) return null;
    return {
      v: 1,
      activeIndex: Math.max(0, Math.floor(data.activeIndex)),
      untitledSeq: Math.max(1, Math.floor(data.untitledSeq)),
      idSeq: Math.max(1, Math.floor(data.idSeq)),
      rows,
    };
  } catch {
    return null;
  }
}

/** Build JSON-serializable snapshot from the current document manager. */
export function buildTabSessionSnapshot(dm: DocumentManager): TabSessionSnapshotV1 {
  const c = dm.getTabSessionCounters();
  return {
    v: 1,
    activeIndex: dm.activeIndex,
    untitledSeq: c.untitledSeq,
    idSeq: c.idSeq,
    rows: dm.documents.map(docToRow),
  };
}

/** Write session to `localStorage` (best-effort; ignores quota / private mode errors). */
export function persistTabSession(dm: DocumentManager): void {
  try {
    const snap = buildTabSessionSnapshot(dm);
    const json = JSON.stringify(snap);
    if (json.length > MAX_STORAGE_CHARS) return;
    localStorage.setItem(TAB_SESSION_KEY, json);
  } catch {
    /* QuotaExceeded, SecurityError, etc. */
  }
}

/**
 * If a stored session exists and validates, replaces all tabs in `dm` (including revoking previews).
 * Returns whether restore ran.
 */
export function tryRestoreTabSessionInto(dm: DocumentManager): boolean {
  try {
    const raw = localStorage.getItem(TAB_SESSION_KEY);
    if (!raw) return false;
    const snap = parseSnapshot(raw);
    if (!snap || snap.rows.length === 0) return false;
    const tabs = snap.rows.map(rowToTab);
    dm.restoreWorkspaceFromPersisted(tabs, snap.activeIndex, snap.untitledSeq, snap.idSeq);
    return true;
  } catch {
    return false;
  }
}

/** Remove stored session (e.g. after explicit “forget session” if added later). */
export function clearTabSessionStorage(): void {
  try {
    localStorage.removeItem(TAB_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export { TAB_SESSION_KEY };
